// features/artifact-journey-map.js — ARTIFACT JOURNEY MAP
// ═══════════════════════════════════════════════════════════
// Renders the user's artifacts as a radial mindmap. Center =
// root; each session = a labeled wedge; versions fan off their
// parent. Click an artifact node → jump to the origin thread
// via onArtifactNavigate (same dispatcher the View button uses).
//
// Composed from:
//   - lib/artifact-map-layout.js  (pure layout math + wedge paths)
//   - hooks/use-artifact-journey-data.js  (data + assembly)
// ═══════════════════════════════════════════════════════════

import { useState } from 'react';
import { useArtifactJourneyData } from '../hooks/use-artifact-journey-data';
import {
  VIEW_SIZE,
  CENTER,
  ROOT_COLOR,
  EDGE_COLOR,
  VERSION_EDGE_COLOR,
  ARTIFACT_TYPE_COLORS,
  buildWedgePath,
  truncate,
} from '../lib/artifact-map-layout';

const LABEL_CROWDING_THRESHOLD = 20; // above this many artifacts, hide non-hovered labels

// Order matters — drives the legend row
const LEGEND_TYPES = [
  { key: 'document', label: 'Document' },
  { key: 'code', label: 'Code' },
  { key: 'web', label: 'Web' },
  { key: 'image', label: 'Image' },
  { key: 'data', label: 'Data' },
  { key: 'upload', label: 'Upload' },
];

export default function ArtifactJourneyMap({ userId, supabase, artifacts, onArtifactNavigate }) {
  const { nodes, edges, sessions, wedges, loading, error } = useArtifactJourneyData({ userId, supabase, artifacts });
  const [hovered, setHovered] = useState(null);
  const [hoveredWedgeId, setHoveredWedgeId] = useState(null);

  // Empty state — no artifacts at all
  if (!loading && (!artifacts || artifacts.length === 0)) {
    return <div style={emptyStyle}>Your thinking will map itself here.</div>;
  }

  const artifactNodes = nodes.filter(n => n.kind === 'artifact');
  const versionNodes = nodes.filter(n => n.kind === 'version');
  const rootNode = nodes.find(n => n.kind === 'root');
  const total = artifactNodes.length;
  const crowded = total > LABEL_CROWDING_THRESHOLD;

  // Which nodes should show a label? When crowded, only the currently
  // hovered node or the hovered session's nodes. Otherwise everything.
  function showLabel(node) {
    if (!crowded) return true;
    if (hovered?.id === node.id) return true;
    if (hoveredWedgeId && node.sessionId === hoveredWedgeId) return true;
    return false;
  }

  return (
    <div style={wrapStyle}>
      {/* ── Legend row ── */}
      <div style={legendRowStyle}>
        {LEGEND_TYPES.map(item => (
          <span key={item.key} style={legendItemStyle}>
            <span style={{ ...legendDotStyle, background: ARTIFACT_TYPE_COLORS[item.key] }} />
            {item.label}
          </span>
        ))}
        <span style={legendSepStyle}>│</span>
        <span style={legendItemStyle}>
          <span style={{ ...legendDotStyle, background: 'transparent', border: '1px solid #9b8fc4', transform: 'rotate(45deg)', borderRadius: 0 }} />
          Synthesis
        </span>
      </div>

      <div style={metaRowStyle}>
        <span>{total} artifacts</span>
        <span style={metaDotStyle}>·</span>
        <span>{sessions?.length || 0} sessions</span>
        {versionNodes.length > 0 && (
          <>
            <span style={metaDotStyle}>·</span>
            <span>{versionNodes.length} versions</span>
          </>
        )}
        {error && <span style={errorStyle}>  {error}</span>}
      </div>

      <div style={svgWrapStyle}>
        <svg viewBox={`0 0 ${VIEW_SIZE} ${VIEW_SIZE}`} style={svgStyle}>
          {/* ── Session wedge backgrounds ── */}
          {wedges.map(w => {
            const isHovered = hoveredWedgeId === w.sessionId;
            const fill = w.isConvergence
              ? (isHovered ? 'rgba(155,143,196,0.09)' : 'rgba(155,143,196,0.05)')
              : (isHovered ? 'rgba(200,169,110,0.06)' : 'rgba(136,136,136,0.035)');
            const stroke = w.isConvergence
              ? 'rgba(155,143,196,0.18)'
              : 'rgba(136,136,136,0.08)';
            return (
              <g key={`w-${w.sessionId}`}
                onMouseEnter={() => setHoveredWedgeId(w.sessionId)}
                onMouseLeave={() => setHoveredWedgeId(prev => (prev === w.sessionId ? null : prev))}
              >
                <path d={buildWedgePath(w)} fill={fill} stroke={stroke} strokeWidth={0.8} />
              </g>
            );
          })}

          {/* ── Edges ── */}
          {edges.map((e, i) => {
            const isSpine = e.kind === 'spine';
            const isVersion = e.kind === 'version';
            return (
              <line
                key={`e-${i}`}
                x1={e.from.x} y1={e.from.y}
                x2={e.to.x} y2={e.to.y}
                stroke={isVersion ? VERSION_EDGE_COLOR : (isSpine ? 'rgba(200,169,110,0.25)' : EDGE_COLOR)}
                strokeWidth={isVersion ? 0.8 : (isSpine ? 1.4 : 1)}
                strokeDasharray={isVersion ? '2,2' : ''}
              />
            );
          })}

          {/* ── Root ── */}
          {rootNode && (
            <g>
              <circle
                cx={rootNode.x} cy={rootNode.y} r={rootNode.radius}
                fill={ROOT_COLOR} fillOpacity={0.12}
                stroke={ROOT_COLOR} strokeWidth={1.4}
              />
              <text
                x={rootNode.x} y={rootNode.y + 4}
                textAnchor="middle"
                fontSize={10} fontFamily="Georgia, serif"
                fill={ROOT_COLOR} fontWeight={700}
              >you</text>
            </g>
          )}

          {/* ── Version satellites ── */}
          {versionNodes.map(n => (
            <circle
              key={n.id}
              cx={n.x} cy={n.y} r={n.radius}
              fill="transparent"
              stroke={VERSION_EDGE_COLOR} strokeWidth={0.9}
            />
          ))}

          {/* ── Artifact nodes + labels ── */}
          {artifactNodes.map(n => {
            const isHovered = hovered?.id === n.id;
            const showMyLabel = showLabel(n);
            const labelOffset = labelOffsetFor(n);
            return (
              <g
                key={n.id}
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => { setHovered(n); setHoveredWedgeId(n.sessionId); }}
                onMouseLeave={() => {
                  setHovered(prev => (prev?.id === n.id ? null : prev));
                  setHoveredWedgeId(prev => (prev === n.sessionId ? null : prev));
                }}
                onClick={() => onArtifactNavigate?.(n.artifact)}
              >
                {n.isConvergence ? (
                  <rect
                    x={n.x - n.radius} y={n.y - n.radius}
                    width={n.radius * 2} height={n.radius * 2}
                    transform={`rotate(45 ${n.x} ${n.y})`}
                    fill={isHovered ? n.color : 'transparent'}
                    fillOpacity={isHovered ? 0.22 : 0.06}
                    stroke={n.color} strokeWidth={isHovered ? 1.8 : 1.3}
                  />
                ) : (
                  <circle
                    cx={n.x} cy={n.y} r={n.radius}
                    fill={isHovered ? n.color : n.color}
                    fillOpacity={isHovered ? 0.22 : 0.08}
                    stroke={n.color} strokeWidth={isHovered ? 1.8 : 1.3}
                  />
                )}
                {showMyLabel && (
                  <text
                    x={n.x + labelOffset.dx} y={n.y + labelOffset.dy}
                    textAnchor={labelOffset.anchor}
                    fontSize={9}
                    fill={isHovered ? n.color : '#888'}
                    fontFamily="Georgia, serif"
                    style={{ pointerEvents: 'none' }}
                  >
                    {truncate(n.artifact.title || 'Untitled', 18)}
                  </text>
                )}
              </g>
            );
          })}

          {/* ── Session labels on the outer arc ── */}
          {wedges.map(w => {
            const textAnchor = textAnchorFor(w.midAngle);
            const isHoveredSession = hoveredWedgeId === w.sessionId;
            return (
              <text
                key={`wl-${w.sessionId}`}
                x={w.labelX} y={w.labelY}
                textAnchor={textAnchor}
                fontSize={10}
                fill={isHoveredSession ? '#c8a96e' : '#666'}
                fontFamily="Georgia, serif"
                fontStyle="italic"
                style={{ pointerEvents: 'none' }}
              >
                {w.label}
              </text>
            );
          })}
        </svg>

        {/* Hover tooltip — pinned to top-right of the canvas */}
        {hovered?.kind === 'artifact' && (
          <div style={tooltipStyle(hovered.color)}>
            <div style={tooltipTypeStyle(hovered.color)}>
              {(hovered.artifact.artifact_type || 'artifact').toString().toLowerCase()}
              {hovered.isConvergence ? ' · synthesis' : ''}
            </div>
            <div style={tooltipTitleStyle}>{hovered.artifact.title || 'Untitled'}</div>
            {hovered.artifact.description && (
              <div style={tooltipDescStyle}>{hovered.artifact.description}</div>
            )}
            <div style={tooltipHintStyle}>click to view →</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Label positioning helpers ──
// For a node at position (x, y) with angle relative to center, pick a
// label anchor side so text doesn't cover the node or fight the edge.
function labelOffsetFor(node) {
  const a = node.angle ?? Math.atan2(node.y - CENTER, node.x - CENTER);
  const gap = node.radius + 4;
  const dx = Math.cos(a) * gap;
  const dy = Math.sin(a) * gap + 3; // vertical nudge for text baseline
  const anchor = Math.cos(a) < -0.2 ? 'end' : (Math.cos(a) > 0.2 ? 'start' : 'middle');
  return { dx, dy, anchor };
}

function textAnchorFor(angle) {
  const c = Math.cos(angle);
  if (c < -0.3) return 'end';
  if (c > 0.3) return 'start';
  return 'middle';
}

// ── Styles ──
const wrapStyle = {
  display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0,
  padding: '12px 24px 24px', background: '#07070a',
};
const legendRowStyle = {
  display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px 14px',
  fontSize: 11, color: '#888', fontFamily: 'Georgia, serif',
  marginBottom: 8,
};
const legendItemStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
};
const legendDotStyle = {
  width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
};
const legendSepStyle = {
  color: '#2a2a2a', margin: '0 2px',
};
const metaRowStyle = {
  display: 'flex', alignItems: 'center', gap: 6,
  fontSize: 11, color: '#555', fontFamily: 'Georgia, serif',
  fontStyle: 'italic', letterSpacing: '0.02em', marginBottom: 10,
};
const metaDotStyle = { color: '#333' };
const errorStyle = { color: '#c46050', marginLeft: 12, fontStyle: 'normal' };
const svgWrapStyle = {
  flex: 1, minHeight: 0, display: 'flex', justifyContent: 'center',
  alignItems: 'center', position: 'relative',
};
const svgStyle = {
  maxWidth: '100%', maxHeight: '100%', display: 'block',
};
const emptyStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  flex: 1, color: 'var(--text-6)', fontSize: 13, fontStyle: 'italic',
  fontFamily: 'Georgia, serif', padding: 40,
};
function tooltipStyle(accent) {
  return {
    position: 'absolute', top: 18, right: 18,
    background: '#111',
    border: `1px solid ${accent || '#888'}55`,
    borderRadius: 6, padding: '10px 14px',
    minWidth: 180, maxWidth: 260,
    pointerEvents: 'none',
    boxShadow: '0 4px 14px rgba(0,0,0,0.6)',
    zIndex: 20,
  };
}
function tooltipTypeStyle(accent) {
  return {
    fontSize: 9, color: accent || '#888', fontFamily: 'Georgia, serif',
    fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
    marginBottom: 6,
  };
}
const tooltipTitleStyle = {
  fontSize: 12, color: '#d4d4d4', fontFamily: 'Georgia, serif',
  lineHeight: 1.45, wordBreak: 'break-word',
};
const tooltipDescStyle = {
  fontSize: 10, color: '#888', fontFamily: 'Georgia, serif',
  lineHeight: 1.45, marginTop: 4, wordBreak: 'break-word',
};
const tooltipHintStyle = {
  fontSize: 9, color: '#555', fontFamily: 'Georgia, serif',
  fontStyle: 'italic', marginTop: 8,
};
